import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ButtonSecondaryMedium from '../common/ButtonSecondary/ButtonSecondaryMedium';
import SwitchComponent from '../common/SwitchComponent/SwitchComponent';
import { Field, Segmented } from './DimensionControls';
import DimensionViewsPanel from './DimensionViewsPanel';
import { openMessageError } from '../common/Message/openMessageError';
import { exportCurrentView } from './exportView';
import { findModuleGroup } from '../../three/core/dimension/AnchorResolver';
import {
  createAutoDimensions,
  createPartAutoDimensions,
} from '../../three/core/dimension/DimensionEngine';
import { partOfObject } from '../../three/core/dimension/PartResolver';
import {
  clearDimensions,
  replaceAutoDimensions,
  requestManualCommand,
  setDimensionMode,
  setManualOptions,
} from '../../store/reducers/dimension.reducer';
import { buildGroupIndex, buildScenePartIndex } from './DimensionOverlay';

// Khoá i18n riêng, bản dịch NGẮN: 5 nút phải vừa một hàng trong khung 340px
const AXIS_OPTIONS = [
  { value: 'auto', label: 'DIM_AXIS_AUTO' },
  { value: 'width', label: 'DIM_AXIS_WIDTH' },
  { value: 'height', label: 'DIM_AXIS_HEIGHT' },
  { value: 'depth', label: 'DIM_AXIS_DEPTH' },
  { value: 'aligned', label: 'DIM_AXIS_ALIGNED' },
];

/** Hướng dẫn theo bước hiện tại của chế độ đo thủ công. */
const stepHint = ({ pendingCount, chain }) => {
  if (!pendingCount) return 'DIM_MANUAL_STEP_1';
  return chain ? 'DIM_MANUAL_STEP_CHAIN' : 'DIM_MANUAL_STEP_2';
};

/**
 * Thanh công cụ DIM nổi trên viewport: Auto DIM, Manual DIM, xoá tất cả.
 * Khi bật Manual DIM hiện thêm khung tuỳ chọn (trục đo, chuỗi) và hướng dẫn.
 * Chỉ hiển thị khi chế độ DIM đang bật.
 */
const DimensionToolbar = ({ sceneManager, selectedModels = [] }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { enabled, items, mode, manual } = useSelector(
    state => state.dimension
  );
  const [showPanel, setShowPanel] = useState(false);

  if (!enabled) return null;

  const handleAutoDim = () => {
    if (!sceneManager?.camera) return;
    const index = buildGroupIndex(sceneManager);
    const partIndex = buildScenePartIndex(sceneManager);

    // Chọn module -> DIM các module đó. Chọn mặt đá / ốp tường -> DIM bộ phận đó.
    // Không chọn gì -> DIM tất cả (module + mặt đá + ốp tường).
    let groups = selectedModels.map(findModuleGroup).filter(Boolean);
    const selectedParts = new Set(
      selectedModels
        .map(o => partOfObject(o) ?? partOfObject(o?.parent))
        .filter(Boolean)
    );
    let partTargets = partIndex.targets.filter(t => selectedParts.has(t.part));
    if (!groups.length && !partTargets.length) {
      groups = [...index.values()];
      partTargets = partIndex.targets;
    }
    groups = [...new Set(groups)];
    if (!groups.length && !partTargets.length) return;

    const { camera } = sceneManager;
    const dom = sceneManager.renderer?.domElement;
    const viewport = dom
      ? { width: dom.clientWidth, height: dom.clientHeight }
      : undefined;
    const allGroups = [...index.values()];
    const moduleItems = groups.flatMap(group =>
      createAutoDimensions(group, group.userData.instanceId, {
        viewPoint: camera.position.clone(),
        camera,
        viewport,
        // Các module khác: DIM tránh đè lên chúng
        obstacles: allGroups.filter(g => g !== group),
      })
    );
    const partItems = partTargets.flatMap(target =>
      createPartAutoDimensions(target, {
        camera,
        viewport,
        // Mặt đá / ốp tường: tránh đè lên mọi module (kể cả tủ bên dưới) và
        // lên các bộ phận khác (vd. DIM mặt đá không nằm trên mặt ốp tường)
        obstacles: [
          ...allGroups,
          ...partIndex.targets.filter(o => o !== target).map(o => o.meshes),
        ],
      })
    );
    dispatch(
      replaceAutoDimensions({
        instanceIds: [
          ...groups.map(g => g.userData.instanceId),
          ...partTargets.map(t => `${t.instanceId}:${t.part}`),
        ],
        items: [...moduleItems, ...partItems],
      })
    );
  };

  const isManual = mode === 'manual';
  const isAnnotate = mode === 'annotate';

  const quickExport = () =>
    exportCurrentView({ sceneManager, dispatch }).catch(() =>
      openMessageError({ content: t('Could not export the image') })
    );

  return (
    <div
      className="flex flex-col gap-8 absolute left-0 w-fit"
      style={{ bottom: 56 }}
    >
      {showPanel && <DimensionViewsPanel sceneManager={sceneManager} />}
      {isAnnotate && (
        <div className="p-[10px] w-[340px] rounded-[8px] bg-[#fff] shadow-dropshadow8 text-body-small text-black-high">
          {t('DIM_NOTE_STEP')}
        </div>
      )}
      {isManual && (
        <div className="flex flex-col gap-8 p-12 w-[340px] rounded-[8px] bg-[#fff] shadow-dropshadow8">
          <div className="p-[10px] rounded-[8px] bg-primary-primaryFill text-body-small text-black-high">
            {t(stepHint(manual))}
          </div>
          <Field
            stacked
            label={t('Axis')}
            hint={t('DIM_HINT_MANUAL_AXIS')}
            hintPlacement="right"
          >
            <Segmented
              fullWidth
              value={manual.axis}
              onChange={axis => dispatch(setManualOptions({ axis }))}
              options={AXIS_OPTIONS.map(o => ({
                ...o,
                label: t(o.label),
              }))}
            />
          </Field>
          <Field
            label={t('Chain')}
            hint={t('DIM_HINT_CHAIN')}
            hintPlacement="right"
          >
            <SwitchComponent
              size="default"
              id="dim-manual-chain"
              value={manual.chain}
              onChange={chain => dispatch(setManualOptions({ chain }))}
            />
          </Field>
          {manual.pendingCount > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-8">
              <span className="text-body-small text-black-medium">
                {manual.chain
                  ? t('DIM_CHAIN_PROGRESS', { count: manual.chainCount })
                  : t('DIM_POINT_PENDING')}
              </span>
              <div className="flex items-center gap-8">
                <ButtonSecondaryMedium
                  title={t('Undo last point')}
                  onClick={() => dispatch(requestManualCommand('undo'))}
                />
                {manual.chain && (
                  <ButtonSecondaryMedium
                    title={t('Done')}
                    onClick={() => dispatch(requestManualCommand('finish'))}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-8 p-8 rounded-[8px] bg-[#fff] shadow-dropshadow8 w-fit max-w-[calc(100vw-32px)] [&_button]:whitespace-nowrap">
        <ButtonSecondaryMedium
          title={
            selectedModels.length ? t('Auto DIM selected') : t('Auto DIM all')
          }
          onClick={handleAutoDim}
        />
        <ButtonSecondaryMedium
          title={t('Manual DIM')}
          className={isManual ? '!bg-primary-primaryFill' : undefined}
          onClick={() =>
            dispatch(setDimensionMode(isManual ? 'idle' : 'manual'))
          }
        />
        <ButtonSecondaryMedium
          title={t('Note')}
          className={isAnnotate ? '!bg-primary-primaryFill' : undefined}
          onClick={() =>
            dispatch(setDimensionMode(isAnnotate ? 'idle' : 'annotate'))
          }
        />
        <ButtonSecondaryMedium
          title={t('Layers & views')}
          className={showPanel ? '!bg-primary-primaryFill' : undefined}
          onClick={() => setShowPanel(v => !v)}
        />
        <ButtonSecondaryMedium
          title={t('Export image')}
          onClick={quickExport}
        />
        <ButtonSecondaryMedium
          title={t('Clear all DIM')}
          disabled={!items.length}
          onClick={() => dispatch(clearDimensions())}
        />
      </div>
    </div>
  );
};

export default DimensionToolbar;
